import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import httpService from "../../../../services/httpServices";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  RefreshCw,
  DollarSign,
  Clock,
  Wallet,
  CreditCard,
  AlertTriangle,
  CheckCircle2,
  Phone,
  UserCheck,
  UserX,
  Network,
  Crown,
  TrendingUp,
} from "lucide-react";
import { resolveRankFromConnect } from "./connectRankUtil";

/**
 * Payment Center — rank-driven, dual-rail (P5-H).
 *
 * Ranked affiliates (Droid / BlockBot / Impact BlockBot) receive Rally tokens
 * and must provide a `rallyWalletAddress`. Unranked affiliates are paid in
 * USD via Stripe and must provide a `stripeConnectedAccountId` (`acct_*`).
 *
 * The Connect United member lookup still runs on this page so users can
 * see their rank status and refresh it if Connect just registered them.
 */
const AffiliatePayoutCenter = ({ affiliate }) => {
  const [profile, setProfile] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state — only two destination inputs now
  const [rallyWalletAddress, setRallyWalletAddress] = useState("");
  const [stripeConnectedAccountId, setStripeConnectedAccountId] = useState("");

  // Connect member lookup
  const [connect, setConnect] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  useEffect(() => {
    fetchAll();
    fetchConnectLookup();
    // eslint-disable-next-line
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [profileRes, dashRes, payoutsRes] = await Promise.all([
        httpService.get("/affiliate/profile"),
        httpService.get("/affiliate/dashboard"),
        httpService.get("/affiliate/payouts?limit=20"),
      ]);
      const p = profileRes.data;
      setProfile(p);
      setDashData(dashRes.data);
      setRallyWalletAddress(p.rallyWalletAddress || "");
      setStripeConnectedAccountId(p.stripeConnectedAccountId || "");
      setPayouts(payoutsRes.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectLookup = async () => {
    setConnectLoading(true);
    try {
      const res = await httpService.get("/affiliate/connect-lookup");
      setConnect(res.data);
    } catch (err) {
      setConnect({
        needsPhone: false,
        error: err.response?.data?.message || "Connect lookup unavailable",
      });
    } finally {
      setConnectLoading(false);
    }
  };

  const submitPhone = async () => {
    if (!phoneInput.trim()) {
      toast.error("Phone number is required");
      return;
    }
    setSavingPhone(true);
    try {
      const res = await httpService.post("/affiliate/connect-lookup/phone", {
        phone: phoneInput.trim(),
      });
      setConnect(res.data);
      setPhoneInput("");
      toast.success("Phone saved");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save phone");
    } finally {
      setSavingPhone(false);
    }
  };

  // Prefer the live Connect lookup rank — the stored affiliate.rank only
  // refreshes on the daily sync job, so it can lag behind Connect by a day.
  const connectRank = resolveRankFromConnect(connect);
  const rank = connectRank?.rank || dashData?.rank || profile?.rank || "none";
  const rankDisplay =
    connectRank?.rankDisplay ||
    dashData?.rankDisplay ||
    profile?.rankDisplay ||
    "Unranked";
  const commissionRate =
    connectRank?.commissionRate ??
    dashData?.commissionRate ??
    profile?.commissionRate ??
    0.2;
  const payoutRail = rank === "none" ? "stripe" : "rally";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (payoutRail === "rally") {
        payload.rallyWalletAddress = rallyWalletAddress.trim();
      } else {
        const trimmed = stripeConnectedAccountId.trim();
        if (trimmed && !/^acct_[A-Za-z0-9]+$/.test(trimmed)) {
          toast.error("Stripe account id must start with 'acct_'");
          setSaving(false);
          return;
        }
        payload.stripeConnectedAccountId = trimmed;
      }
      await httpService.put("/affiliate/payout-settings", payload);
      toast.success("Payment details saved");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-[#7700FF] animate-spin" />
          <p className="text-gray-400">Loading payment center...</p>
        </div>
      </div>
    );
  }

  const pending = dashData?.pendingBalance || 0;
  const threshold = dashData?.payoutThreshold || 100;
  const lifetime = dashData?.lifetimeEarnings || 0;
  const destinationSet =
    payoutRail === "rally" ? !!rallyWalletAddress : !!stripeConnectedAccountId;
  const lockedForEdit = !!profile?.payoutLockedAt;

  return (
    <div className="space-y-6 p-3 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/affiliate"
            className="flex items-center justify-center w-10 h-10 bg-white/5 border border-white/10 hover:bg-white/10 text-gray-400 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h2 className="text-2xl font-bold text-white">Payment Center</h2>
            <p className="text-gray-400 text-sm">
              Destination, balance, and payment history
            </p>
          </div>
        </div>
      </div>

      {/* Rank + commission summary */}
      <div className="bg-gradient-to-r from-[#7700FF]/15 to-[#00c853]/10 border border-[#7700FF]/30 rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#7700FF]/25 flex items-center justify-center">
              <Crown className="w-6 h-6 text-[#7700FF]" />
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">
                Your Rank
              </p>
              <p className="text-2xl font-bold text-white">{rankDisplay}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 md:justify-end">
            <div className="w-11 h-11 rounded-xl bg-[#00c853]/25 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-[#00c853]" />
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">
                Commission Rate
              </p>
              <p className="text-2xl font-bold text-white">
                {Math.round(commissionRate * 100)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Connect status */}
      <ConnectStatusCard
        connect={connect}
        connectLoading={connectLoading}
        phoneInput={phoneInput}
        setPhoneInput={setPhoneInput}
        submitPhone={submitPhone}
        savingPhone={savingPhone}
        onRefresh={fetchConnectLookup}
      />

      {/* Balance cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BalanceCard
          icon={DollarSign}
          label="Pending Balance"
          value={`$${pending.toFixed(2)}`}
          subvalue={`$${threshold} minimum to release`}
          color="#ffa500"
        />
        <BalanceCard
          icon={CheckCircle2}
          label="Lifetime Earnings"
          value={`$${lifetime.toFixed(2)}`}
          subvalue="Paid out since enrollment"
          color="#00c853"
        />
        <BalanceCard
          icon={Clock}
          label="Next Payment"
          value={pending >= threshold ? "Ready to queue" : "Below minimum"}
          subvalue={
            pending >= threshold
              ? destinationSet
                ? "Will be queued on next admin cycle"
                : "Add your destination below"
              : `$${(threshold - pending).toFixed(2)} to go`
          }
          color="#7700FF"
        />
      </div>

      {/* Destination form */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#7700FF]/20 flex items-center justify-center">
            {payoutRail === "rally" ? (
              <Wallet className="w-5 h-5 text-[#7700FF]" />
            ) : (
              <CreditCard className="w-5 h-5 text-[#7700FF]" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              {payoutRail === "rally" ? "Rally Wallet Address" : "Stripe Connect Account"}
            </h3>
            <p className="text-xs text-gray-400">
              {payoutRail === "rally"
                ? "Rally tokens are sent on the Win blockchain to this wallet."
                : "USD payments are sent via Stripe to this connected account."}
            </p>
          </div>
        </div>

        {lockedForEdit && (
          <div className="mb-4 bg-[#ffa500]/10 border border-[#ffa500]/30 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[#ffa500] flex-shrink-0 mt-0.5" />
            <p className="text-[#ffa500] text-sm">
              A payment is currently in flight. Destination edits are locked until the
              payment finishes processing.
            </p>
          </div>
        )}

        {payoutRail === "rally" ? (
          <input
            type="text"
            value={rallyWalletAddress}
            onChange={(e) => setRallyWalletAddress(e.target.value)}
            disabled={lockedForEdit}
            placeholder="Enter your Rally wallet address"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#7700FF] disabled:opacity-50 font-mono text-sm"
          />
        ) : (
          <input
            type="text"
            value={stripeConnectedAccountId}
            onChange={(e) => setStripeConnectedAccountId(e.target.value)}
            disabled={lockedForEdit}
            placeholder="acct_..."
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#7700FF] disabled:opacity-50 font-mono text-sm"
          />
        )}

        <div className="mt-4 flex justify-end">
          <button
            onClick={save}
            disabled={saving || lockedForEdit}
            className="bg-gradient-to-r from-[#7700FF] to-[#00c853] hover:opacity-90 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg font-bold text-sm transition-opacity"
          >
            {saving ? "Saving..." : "Save payment details"}
          </button>
        </div>

        {/* Info about the rail */}
        <div className="mt-4 pt-4 border-t border-white/10 text-xs text-gray-400">
          {payoutRail === "rally" ? (
            <>
              <strong className="text-[#00c853]">Rally tokens</strong> — Admin sends
              manually on-chain from the admin panel. Automated BlockBot Pay (payment
              within 3 days of each qualifying transaction on the Win blockchain) is
              <em> Coming Soon</em>.
            </>
          ) : (
            <>
              <strong className="text-[#3b82f6]">Stripe</strong> — USD payments via
              Stripe Connect. Your commission is 20% because you're not yet in the
              Connect United directory — once you're verified, your rate and rail will
              update automatically.
            </>
          )}
        </div>
      </div>

      {/* Payment history */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Payment History</h3>
        {payouts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400 mb-1">No payments yet</p>
            <p className="text-gray-500 text-sm">
              Payments are released once your balance reaches ${threshold}.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 text-gray-500 font-medium">Date</th>
                  <th className="text-left py-3 text-gray-500 font-medium">Rail</th>
                  <th className="text-left py-3 text-gray-500 font-medium">Reference</th>
                  <th className="text-right py-3 text-gray-500 font-medium">USD</th>
                  <th className="text-right py-3 text-gray-500 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => {
                  const rail = p.rail || "rally";
                  const ref =
                    rail === "rally"
                      ? p.blockchainTxId || p.walletAddress || "—"
                      : p.stripePayoutId || "—";
                  return (
                    <tr key={p._id} className="border-b border-white/5">
                      <td className="py-3 text-gray-300">
                        {new Date(p.paidAt || p.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                            rail === "rally"
                              ? "bg-[#00c853]/20 text-[#00c853]"
                              : "bg-[#3b82f6]/20 text-[#3b82f6]"
                          }`}
                        >
                          {rail}
                        </span>
                      </td>
                      <td className="py-3 text-gray-300 font-mono text-xs truncate max-w-[220px]">
                        {ref}
                      </td>
                      <td className="py-3 text-right font-bold text-white">
                        ${(p.usdAmount || 0).toFixed(2)}
                      </td>
                      <td className="py-3 text-right">
                        <span
                          className={`rounded-full text-xs font-bold px-3 py-1 ${
                            p.status === "paid"
                              ? "bg-[#00c853]/20 text-[#00c853]"
                              : p.status === "failed" || p.status === "cancelled"
                              ? "bg-red-500/20 text-red-400"
                              : "bg-[#ffa500]/20 text-[#ffa500]"
                          }`}
                        >
                          {p.status.replace(/_/g, " ")}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Subcomponents ─────────────────────────────────────────────────────

const BalanceCard = ({ icon: Icon, label, value, subvalue, color }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
    <div className="flex items-center gap-3 mb-3">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-sm text-gray-400">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white mb-1">{value}</p>
    <p className="text-xs text-gray-500">{subvalue}</p>
  </div>
);

const ConnectStatusCard = ({
  connect,
  connectLoading,
  phoneInput,
  setPhoneInput,
  submitPhone,
  savingPhone,
  onRefresh,
}) => {
  if (connectLoading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
        <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
        <p className="text-sm text-gray-400">Checking Connect United status...</p>
      </div>
    );
  }
  if (!connect) return null;

  if (connect.needsPhone) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-[#3b82f6]/20 flex items-center justify-center flex-shrink-0">
            <Phone className="w-5 h-5 text-[#3b82f6]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-semibold">Add your phone to check Connect status</h4>
            <p className="text-sm text-gray-400">
              If you're a Connect United member, your rank (Droid / BlockBot / Impact
              BlockBot) unlocks a higher commission rate and Rally token payouts.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="tel"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="Phone number"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-[#7700FF]"
          />
          <button
            onClick={submitPhone}
            disabled={savingPhone}
            className="bg-gradient-to-r from-[#7700FF] to-[#00c853] hover:opacity-90 disabled:opacity-50 text-white px-4 py-2.5 rounded-lg font-bold text-sm transition-opacity"
          >
            {savingPhone ? "Checking..." : "Check status"}
          </button>
        </div>
      </div>
    );
  }

  if (connect.error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 flex items-center gap-3">
        <UserX className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-red-400 font-semibold">Connect lookup error</p>
          <p className="text-sm text-gray-400">{connect.error}</p>
        </div>
        <button
          onClick={onRefresh}
          className="text-xs px-3 py-1.5 bg-white/5 border border-white/10 hover:bg-white/10 rounded-lg text-gray-300 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const found = connect.lookup?.found;
  const member = connect.lookup?.member;
  return (
    <div
      className={`bg-white/5 border rounded-2xl p-4 flex items-center gap-3 ${
        found ? "border-[#00c853]/30" : "border-white/10"
      }`}
    >
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          found ? "bg-[#00c853]/20" : "bg-white/10"
        }`}
      >
        {found ? (
          <UserCheck className="w-5 h-5 text-[#00c853]" />
        ) : (
          <Network className="w-5 h-5 text-gray-400" />
        )}
      </div>
      <div className="flex-1">
        <p className="text-white font-semibold">
          {found
            ? `Connect member found — rank: ${member?.rank || "(not set)"}`
            : "Not currently in the Connect United directory"}
        </p>
        <p className="text-xs text-gray-400">
          {found
            ? `Member #${member?.memberNumber || "—"}${
                member?.sponsorName ? ` · Sponsor: ${member.sponsorName}` : ""
              }`
            : "Your commission is 20%. Join Connect United to upgrade your rank."}
        </p>
      </div>
      <button
        onClick={onRefresh}
        className="text-xs px-3 py-1.5 bg-white/5 border border-white/10 hover:bg-white/10 rounded-lg text-gray-300 transition-colors"
      >
        Refresh
      </button>
    </div>
  );
};

export default AffiliatePayoutCenter;
